import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Badge, Button, Form, Table, ProgressBar } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import alertDashboardService from '../../services/alertDashboardService';
import { openAlert } from '../../utils/common';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const AlertDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  // 데이터 로드
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await alertDashboardService.getOverview(timeRange);
      
      if (result.success) {
        setOverview(result.data);
        setLastUpdated(new Date());
      } else {
        setError(result.message || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || '데이터를 불러오는데 실패했습니다.';
      setError(errorMessage);
      openAlert({ title: '알림', description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // 초기 로드 및 자동 새로고침
  useEffect(() => {
    loadData();
    
    if (autoRefresh) {
      intervalRef.current = setInterval(loadData, 30000);
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadData, autoRefresh]);

  // 상태 Badge 반환
  const getStatusBadge = (status) => {
    const variants = {
      'HEALTHY': 'success',
      'ACTIVE': 'success',
      'WARNING': 'warning',
      'DEGRADED': 'warning',
      'UNHEALTHY': 'danger',
      'DISABLED': 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // 채널 아이콘 반환
  const getChannelIcon = (type) => {
    const icons = {
      'email': '📧',
      'sms': '📱',
      'slack': '💬',
      'push': '🔔',
      'database': '💾'
    };
    return icons[type] || '📢';
  };

  // 시간 포맷
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}초 전`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}분 전`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}시간 전`;
    return date.toLocaleDateString('ko-KR') + ' ' + date.toLocaleTimeString('ko-KR');
  };

  // 차트 데이터 생성
  const getAlertTrendChartData = () => {
    if (!overview?.trends) return null;
    
    return {
      labels: overview.trends.labels || [],
      datasets: [
        {
          label: '보안 알림',
          data: overview.trends.security_alerts || [],
          borderColor: '#dc3545',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          tension: 0.3
        },
        {
          label: '시스템 알림',
          data: overview.trends.system_alerts || [],
          borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.1)',
          tension: 0.3
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };

  if (loading && !overview) {
    return (
      <SectionContainer title="알림 채널 대시보드">
        <div className="text-center py-5">
          <Spinner />
        </div>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer title="알림 채널 대시보드">
      {/* 헤더 컨트롤 */}
      <Row className="mb-3 align-items-center">
        <Col>
          {lastUpdated && (
            <small className="text-muted">
              마지막 업데이트: {lastUpdated.toLocaleTimeString()}
            </small>
          )}
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <Form.Select 
            size="sm" 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ width: '120px' }}
          >
            <option value="1h">1시간</option>
            <option value="6h">6시간</option>
            <option value="24h">24시간</option>
            <option value="7d">7일</option>
            <option value="30d">30일</option>
          </Form.Select>
          <Form.Check 
            type="switch"
            id="auto-refresh"
            label="자동"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={loadData}
            disabled={loading}
          >
            {loading ? '로딩...' : '새로고침'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* 요약 카드 */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">📢 오늘의 알림</div>
              <h2 className="mb-0 text-primary">
                {overview?.summary?.total_alerts_today || 0}
              </h2>
              <small className="text-muted">
                해결: {overview?.summary?.resolved_alerts || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-danger">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">🚨 활성 알림</div>
              <h2 className="mb-0 text-danger">
                {overview?.summary?.active_alerts?.total || 0}
              </h2>
              <small className="text-muted">
                Critical: {overview?.summary?.active_alerts?.critical || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-success">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">🛡️ 무결성 점수</div>
              <h2 className="mb-0 text-success">
                {overview?.summary?.integrity_score || 0}%
              </h2>
              <small className="text-muted">
                실패: {overview?.summary?.failed_integrity_checks || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-warning">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">⚠️ 보안 이벤트</div>
              <h2 className="mb-0 text-warning">
                {overview?.summary?.security_events_today || 0}
              </h2>
              <small className="text-muted">24시간 기준</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 채널 상태 & 시스템 상태 */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <strong>📡 채널 상태</strong>
            </Card.Header>
            <Card.Body>
              {overview?.channel_status ? (
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>채널</th>
                      <th>상태</th>
                      <th>응답시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(overview.channel_status).map(([channel, info]) => (
                      <tr key={channel}>
                        <td>{getChannelIcon(channel)} {channel.toUpperCase()}</td>
                        <td>{getStatusBadge(info.status)}</td>
                        <td>{info.response_time || '-'}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center mb-0">채널 정보가 없습니다.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <strong>💻 시스템 상태</strong>
            </Card.Header>
            <Card.Body>
              {overview?.integrity_monitoring?.system_metrics ? (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>CPU 사용량</small>
                      <small>{overview.integrity_monitoring.system_metrics.cpu_usage || 0}%</small>
                    </div>
                    <ProgressBar 
                      now={overview.integrity_monitoring.system_metrics.cpu_usage || 0} 
                      variant={overview.integrity_monitoring.system_metrics.cpu_usage > 80 ? 'danger' : 'success'}
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>메모리 사용량</small>
                      <small>{overview.integrity_monitoring.system_metrics.memory_usage || 0}%</small>
                    </div>
                    <ProgressBar 
                      now={overview.integrity_monitoring.system_metrics.memory_usage || 0} 
                      variant={overview.integrity_monitoring.system_metrics.memory_usage > 80 ? 'danger' : 'info'}
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>가용성 (Uptime)</small>
                      <small>{overview.integrity_monitoring.system_metrics.uptime || 0}%</small>
                    </div>
                    <ProgressBar 
                      now={overview.integrity_monitoring.system_metrics.uptime || 0} 
                      variant="success"
                    />
                  </div>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">API 응답시간</small>
                    <small>{overview.integrity_monitoring.system_metrics.api_response_time || 0}ms</small>
                  </div>
                </>
              ) : (
                <p className="text-muted text-center mb-0">시스템 정보가 없습니다.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 알림 추이 차트 */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <strong>📈 알림 추이</strong>
            </Card.Header>
            <Card.Body>
              {getAlertTrendChartData() ? (
                <div style={{ height: '300px' }}>
                  <Line data={getAlertTrendChartData()} options={chartOptions} />
                </div>
              ) : (
                <p className="text-muted text-center mb-0">차트 데이터가 없습니다.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 최근 중요 알림 */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <strong>🔔 최근 중요 알림</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>시간</th>
                    <th>유형</th>
                    <th>위험도</th>
                    <th>내용</th>
                  </tr>
                </thead>
                <tbody>
                  {overview?.recent_critical?.length > 0 ? (
                    overview.recent_critical.map((alert, index) => (
                      <tr key={index}>
                        <td>{formatTime(alert.created_at)}</td>
                        <td><Badge bg="secondary">{alert.type}</Badge></td>
                        <td>
                          <Badge bg={
                            alert.risk_level === 'CRITICAL' ? 'danger' :
                            alert.risk_level === 'HIGH' ? 'warning' : 'info'
                          }>
                            {alert.risk_level}
                          </Badge>
                        </td>
                        <td>{alert.title}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        최근 중요 알림이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </SectionContainer>
  );
};

export default AlertDashboardPage;
